import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';

interface RenameAttachmentModalProps {
    show: boolean;
    /** The original file name; shown read-only and used when the display name is cleared. */
    fileName: string;
    /** The current custom display name, if any. */
    displayName?: string;
    onHide: () => void;
    /** Receives the trimmed display name, or undefined when the field was cleared (fall back to the file name). */
    onConfirm: (displayName: string | undefined) => Promise<void>;
    submitting?: boolean;
}

/**
 * Changes an uploaded attachment's display name (`displayName` on the
 * attachment node). The stored file and its original `fileName` are never
 * touched. Shared by project attachments, work-order attachments and lease
 * contracts.
 */
export const RenameAttachmentModal: React.FC<RenameAttachmentModalProps> = ({
    show,
    fileName,
    displayName,
    onHide,
    onConfirm,
    submitting = false,
}) => {
    const [value, setValue] = useState('');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (show) {
            setValue(displayName ?? '');
            setError(null);
        }
    }, [show, displayName]);

    const handleConfirm = async () => {
        setError(null);
        try {
            await onConfirm(value.trim() || undefined);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte byta namn på bilagan');
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Byt namn på bilaga</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                <Form.Group className="mb-3">
                    <Form.Label>Filnamn</Form.Label>
                    <Form.Control type="text" value={fileName} disabled readOnly />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>Visningsnamn</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder={fileName}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        disabled={submitting}
                        autoFocus
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                void handleConfirm();
                            }
                        }}
                    />
                    <Form.Text className="text-muted">
                        Lämna tomt för att använda filnamnet
                    </Form.Text>
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={submitting}>
                    Avbryt
                </Button>
                <Button variant="primary" onClick={() => { void handleConfirm(); }} disabled={submitting}>
                    Spara
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default RenameAttachmentModal;
